/**
 * PoseRenderer - Utility for drawing MediaPipe pose skeleton on canvas
 * Used by CanvasPoseOverlayPlayer for client-side pose visualization
 */

// MediaPipe 33-landmark pose connections
export const POSE_CONNECTIONS = [
  // Face
  [0, 1], [1, 2], [2, 3], [3, 7],      // Left eye to left ear
  [0, 4], [4, 5], [5, 6], [6, 8],      // Right eye to right ear
  [9, 10],                              // Mouth

  // Torso
  [11, 12],                             // Shoulders
  [11, 23], [12, 24],                   // Shoulders to hips
  [23, 24],                             // Hips

  // Left arm
  [11, 13], [13, 15],                   // Shoulder -> elbow -> wrist
  [15, 17], [15, 19], [15, 21],         // Wrist to hand
  [17, 19],                             // Pinky to index

  // Right arm
  [12, 14], [14, 16],                   // Shoulder -> elbow -> wrist
  [16, 18], [16, 20], [16, 22],         // Wrist to hand
  [18, 20],                             // Pinky to index

  // Left leg
  [23, 25], [25, 27],                   // Hip -> knee -> ankle
  [27, 29], [27, 31],                   // Ankle to foot
  [29, 31],                             // Heel to toe

  // Right leg
  [24, 26], [26, 28],                   // Hip -> knee -> ankle
  [28, 30], [28, 32],                   // Ankle to foot
  [30, 32]                              // Heel to toe
];

// Landmark names for debugging
export const LANDMARK_NAMES = [
  'nose', 'left_eye_inner', 'left_eye', 'left_eye_outer',
  'right_eye_inner', 'right_eye', 'right_eye_outer',
  'left_ear', 'right_ear', 'mouth_left', 'mouth_right',
  'left_shoulder', 'right_shoulder', 'left_elbow', 'right_elbow',
  'left_wrist', 'right_wrist', 'left_pinky', 'right_pinky',
  'left_index', 'right_index', 'left_thumb', 'right_thumb',
  'left_hip', 'right_hip', 'left_knee', 'right_knee',
  'left_ankle', 'right_ankle', 'left_heel', 'right_heel',
  'left_foot_index', 'right_foot_index'
];

/**
 * Draw pose skeleton on canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {Object} poseData - Pose data with pose_landmarks array
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {Object} options - Drawing options
 */
export function drawPoseSkeleton(ctx, poseData, width, height, options = {}) {
  const {
    connectionColor = '#00FF00',
    landmarkColor = '#00FF00',
    connectionWidth = 2,
    landmarkRadius = 4,
    confidenceThreshold = 0.5,
    showConfidenceColors = false
  } = options;

  const landmarks = poseData?.pose_landmarks;

  if (!landmarks || landmarks.length < 33) {
    return;
  }

  // Draw connections first (skeleton lines)
  ctx.lineWidth = connectionWidth;

  for (const [startIdx, endIdx] of POSE_CONNECTIONS) {
    const start = landmarks[startIdx];
    const end = landmarks[endIdx];

    if (!start || !end) continue;

    const startConf = start.confidence ?? start.visibility ?? 0;
    const endConf = end.confidence ?? end.visibility ?? 0;

    if (startConf > confidenceThreshold && endConf > confidenceThreshold) {
      // Set color based on confidence if enabled
      if (showConfidenceColors) {
        const avgConf = (startConf + endConf) / 2;
        ctx.strokeStyle = getConfidenceColor(avgConf);
      } else {
        ctx.strokeStyle = connectionColor;
      }

      ctx.beginPath();
      ctx.moveTo(start.x * width, start.y * height);
      ctx.lineTo(end.x * width, end.y * height);
      ctx.stroke();
    }
  }

  // Draw landmarks (joint points) on top
  for (let i = 0; i < landmarks.length; i++) {
    const landmark = landmarks[i];
    if (!landmark) continue;

    const conf = landmark.confidence ?? landmark.visibility ?? 0;

    if (conf > confidenceThreshold) {
      if (showConfidenceColors) {
        ctx.fillStyle = getConfidenceColor(conf);
      } else {
        ctx.fillStyle = landmarkColor;
      }

      ctx.beginPath();
      ctx.arc(
        landmark.x * width,
        landmark.y * height,
        landmarkRadius,
        0,
        2 * Math.PI
      );
      ctx.fill();
    }
  }
}

/**
 * Get color based on confidence level
 * @param {number} confidence - Confidence value 0-1
 * @returns {string} CSS color string
 */
function getConfidenceColor(confidence) {
  if (confidence >= 0.8) {
    return '#00FF00'; // Green - high confidence
  } else if (confidence >= 0.6) {
    return '#FFFF00'; // Yellow - medium confidence
  } else {
    return '#FF8800'; // Orange - low confidence
  }
}

/**
 * Draw frame info overlay on canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} frameNumber - Current frame number
 * @param {number} totalFrames - Total frames in video
 * @param {number} confidence - Pose confidence for this frame
 * @param {Object} options - Drawing options
 */
export function drawFrameInfo(ctx, frameNumber, totalFrames, confidence, options = {}) {
  const {
    x = 10,
    y = 10,
    backgroundColor = 'rgba(0, 0, 0, 0.6)',
    textColor = '#FFFFFF',
    fontSize = 14
  } = options;

  const padding = 10;
  const lineHeight = fontSize + 4;
  const lines = [
    `Frame: ${frameNumber}/${totalFrames}`,
    `Confidence: ${(confidence * 100).toFixed(1)}%`
  ];

  // Calculate box dimensions
  ctx.font = `${fontSize}px monospace`;
  const maxWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
  const boxWidth = maxWidth + padding * 2;
  const boxHeight = lines.length * lineHeight + padding * 2;

  // Draw semi-transparent background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(x, y, boxWidth, boxHeight);

  // Draw text
  ctx.fillStyle = textColor;
  ctx.font = `${fontSize}px monospace`;
  lines.forEach((line, i) => {
    ctx.fillText(line, x + padding, y + padding + (i + 1) * lineHeight - 4);
  });
}

/**
 * Draw "No Pose" indicator when pose data is not available
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {number} frameNumber - Current frame number
 */
export function drawNoPoseIndicator(ctx, width, height, frameNumber) {
  const text = `Frame ${frameNumber}: No pose data`;

  ctx.font = '16px monospace';
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';

  const metrics = ctx.measureText(text);
  const x = 10;
  const y = height - 40;

  ctx.fillRect(x - 5, y - 20, metrics.width + 10, 30);

  ctx.fillStyle = '#888888';
  ctx.fillText(text, x, y);
}

/**
 * Draw sync status indicator
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} width - Canvas width
 * @param {boolean} isUsingRVFC - Whether using requestVideoFrameCallback
 * @param {number} missedFrames - Number of missed frames
 */
export function drawSyncStatus(ctx, width, isUsingRVFC, missedFrames = 0) {
  const statusText = isUsingRVFC
    ? `Sync: rVFC (frame-accurate)`
    : `Sync: rAF (fallback)`;

  const color = isUsingRVFC ? '#00FF00' : '#FFFF00';

  ctx.font = '12px monospace';
  const metrics = ctx.measureText(statusText);
  const x = width - metrics.width - 15;
  const y = 10;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.fillRect(x - 5, y, metrics.width + 10, 20);

  ctx.fillStyle = color;
  ctx.fillText(statusText, x, y + 14);

  if (missedFrames > 0) {
    const missedText = `Missed: ${missedFrames}`;
    ctx.fillStyle = '#FF8800';
    ctx.fillText(missedText, x, y + 30);
  }
}

export default {
  drawPoseSkeleton,
  drawFrameInfo,
  drawNoPoseIndicator,
  drawSyncStatus,
  POSE_CONNECTIONS,
  LANDMARK_NAMES
};
